import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@workspace/amarmeds-ds/components/ui/badge";
import { Button } from "@workspace/amarmeds-ds/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@workspace/amarmeds-ds/components/ui/card";
import { Input } from "@workspace/amarmeds-ds/components/ui/input";

import { api, ApiError } from "../api";
import { dhakaTime } from "../format";
import { prepareImage } from "./images";
import { imageSrc, type OpsBanner, type StoreMeta } from "./types";

// The banners at the top of the app's Store tab. Best at 2.2 : 1 (e.g. 1320 × 600).

const errorText = (err: unknown) => (err instanceof ApiError ? err.code.replace(/_/g, " ") : err instanceof Error ? err.message : "Something went wrong.");

/** A <input type="datetime-local"> value (Dhaka time) as ISO, or null. */
const fromLocal = (v: string) => (v ? new Date(`${v}:00+06:00`).toISOString() : null);
const toLocal = (iso: string | null) => (iso ? new Date(new Date(iso).getTime() + 6 * 3_600_000).toISOString().slice(0, 16) : "");

type Form = { linkType: string; linkValue: string; altText: string; position: string; active: boolean; startsAt: string; endsAt: string };
const blank: Form = { linkType: "none", linkValue: "", altText: "", position: "0", active: true, startsAt: "", endsAt: "" };

export function BannersTab({ meta }: { meta: StoreMeta | null }) {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["store-banners"], queryFn: () => api<{ banners: OpsBanner[] }>("/ops/store/banners") });
  const [editing, setEditing] = useState<OpsBanner | "new" | null>(null);
  const toggle = useMutation({
    mutationFn: (b: OpsBanner) => api(`/ops/store/banners/${b.id}`, { method: "PATCH", body: { active: !b.active } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["store-banners"] }),
  });

  if (editing) return <BannerForm meta={meta} banner={editing === "new" ? null : editing} onDone={() => setEditing(null)} />;
  const now = Date.now();
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-base">Banners</CardTitle>
        <Button size="sm" onClick={() => setEditing("new")}>
          Add banner
        </Button>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground">Shown in order at the top of the Store, sliding every few seconds. Best at 2.2 : 1, e.g. 1320 × 600.</p>
        {list.data?.banners.map((b) => {
          const live = b.active && (!b.startsAt || Date.parse(b.startsAt) <= now) && (!b.endsAt || Date.parse(b.endsAt) > now);
          return (
            <div key={b.id} className="flex flex-wrap items-center gap-3 border-b pb-3 last:border-0">
              <img src={imageSrc(b.imageUrl)!} alt={b.altText} className="h-20 w-44 rounded-md border object-cover" />
              <div className="min-w-48 flex-1 space-y-1">
                <div className="flex flex-wrap gap-1">
                  <Badge variant={live ? "default" : "outline"}>{live ? "Showing" : b.active ? "Scheduled / ended" : "Off"}</Badge>
                  <Badge variant="outline">#{b.position}</Badge>
                </div>
                <div>{b.altText || <span className="text-muted-foreground">No description</span>}</div>
                <div className="text-muted-foreground">
                  Opens: {b.link.type === "none" ? "nothing" : `${b.link.type} ${b.link.value}`}
                  {(b.startsAt || b.endsAt) && ` · ${b.startsAt ? dhakaTime(b.startsAt) : "now"} → ${b.endsAt ? dhakaTime(b.endsAt) : "no end"}`}
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => toggle.mutate(b)}>
                  {b.active ? "Turn off" : "Turn on"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(b)}>
                  Edit
                </Button>
              </div>
            </div>
          );
        })}
        {list.data && list.data.banners.length === 0 && <p className="text-muted-foreground">No banners yet.</p>}
      </CardContent>
    </Card>
  );
}

function BannerForm({ meta, banner, onDone }: { meta: StoreMeta | null; banner: OpsBanner | null; onDone: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState<Form>(() =>
    banner
      ? {
          linkType: banner.link.type,
          linkValue: banner.link.value ?? "",
          altText: banner.altText,
          position: String(banner.position),
          active: banner.active,
          startsAt: toLocal(banner.startsAt),
          endsAt: toLocal(banner.endsAt),
        }
      : blank,
  );
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(banner ? imageSrc(banner.imageUrl) : null);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((x) => ({ ...x, [k]: v }));

  const save = useMutation({
    mutationFn: async () => {
      if (!banner && !file) throw new Error("Choose an image.");
      const img = file ? await prepareImage(file, 1600, 0.85) : null;
      const body = {
        ...(img ? { data: img.data, width: img.width, height: img.height } : {}),
        linkType: f.linkType,
        linkValue: f.linkType === "none" ? null : f.linkValue,
        altText: f.altText,
        position: Number(f.position) || 0,
        active: f.active,
        startsAt: fromLocal(f.startsAt),
        endsAt: fromLocal(f.endsAt),
      };
      if (banner) await api(`/ops/store/banners/${banner.id}`, { method: "PATCH", body });
      else await api("/ops/store/banners", { body });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["store-banners"] });
      onDone();
    },
    onError: (err) => setError(errorText(err)),
  });
  const remove = useMutation({
    mutationFn: () => api(`/ops/store/banners/${banner!.id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["store-banners"] });
      onDone();
    },
    onError: (err) => setError(errorText(err)),
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-base">{banner ? "Edit banner" : "Add a banner"}</CardTitle>
        <Button size="sm" variant="outline" onClick={onDone}>
          Back
        </Button>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="aspect-[2.2/1] w-full max-w-xl overflow-hidden rounded-lg border bg-muted">
          {preview && <img src={preview} alt="" className="h-full w-full object-cover" />}
        </div>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => {
            const picked = e.target.files?.[0] ?? null;
            setFile(picked);
            if (picked) setPreview(URL.createObjectURL(picked));
          }}
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="space-y-1">
            <span className="font-medium">Opens</span>
            <select className="h-9 w-full rounded-md border bg-background px-2" value={f.linkType} onChange={(e) => set("linkType", e.target.value)}>
              <option value="none">Nothing</option>
              <option value="category">A department</option>
              <option value="product">A product (its id)</option>
              <option value="url">A link or app screen</option>
            </select>
          </label>
          {f.linkType === "category" ? (
            <label className="space-y-1">
              <span className="font-medium">Department</span>
              <select className="h-9 w-full rounded-md border bg-background px-2" value={f.linkValue} onChange={(e) => set("linkValue", e.target.value)}>
                <option value="">Choose…</option>
                {meta?.categories.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.en}
                  </option>
                ))}
              </select>
            </label>
          ) : f.linkType !== "none" ? (
            <label className="space-y-1">
              <span className="font-medium">{f.linkType === "product" ? "Product id" : "Link"}</span>
              <Input value={f.linkValue} placeholder={f.linkType === "product" ? "9000123" : "https://… or /scan"} onChange={(e) => set("linkValue", e.target.value)} />
            </label>
          ) : (
            <div />
          )}
          <label className="space-y-1">
            <span className="font-medium">Description (read out by screen readers)</span>
            <Input value={f.altText} onChange={(e) => set("altText", e.target.value)} />
          </label>
          <label className="space-y-1">
            <span className="font-medium">Order</span>
            <Input inputMode="numeric" value={f.position} onChange={(e) => set("position", e.target.value)} />
          </label>
          <label className="space-y-1">
            <span className="font-medium">Show from (Dhaka time, optional)</span>
            <Input type="datetime-local" value={f.startsAt} onChange={(e) => set("startsAt", e.target.value)} />
          </label>
          <label className="space-y-1">
            <span className="font-medium">Until (optional)</span>
            <Input type="datetime-local" value={f.endsAt} onChange={(e) => set("endsAt", e.target.value)} />
          </label>
        </div>
        <label className="flex items-center gap-2">
          <input type="checkbox" className="h-4 w-4" checked={f.active} onChange={(e) => set("active", e.target.checked)} />
          On
        </label>
        {error && <p className="text-destructive">{error}</p>}
        <div className="flex gap-2">
          <Button disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
          {banner && (
            <Button variant="outline" disabled={remove.isPending} onClick={() => window.confirm("Delete this banner?") && remove.mutate()}>
              Delete
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
